import { useToast } from '@chakra-ui/react';
import { head } from 'ramda';
import { useNavigate } from 'react-router-dom';

import { CreateFolderValidationDialog } from '../../components/create-folder-validation-dialog/create-folder-validation-dialog';
import { FolderNameDialog } from '../../components/folder-name-dialog/folder-name-dialog';
import * as ToastConfigs from '../../configs/toast';
import { useImperativeModal } from '../../context/imperative-modal/imperative-modal';
import {
  useCreateFolderMutation,
  useValidateFilenamesMutation,
  ValidatedFilename,
} from '../../graphql/generated';
import { generateNewDirPath } from '../../lib/storage-object/storage-object';
import { navigateToDirectory } from '../../pages/file-browser-page/file-browser-page';
import { formatDirPath } from '../../utils/format-dir-path/format-dir-path';
import { getSuggestedFilepathAndFilename } from '../use-file-upload/file-validation/validation-utils';
import { getValidateFolderMutationInput } from './validation-utils';

type ValidateFolderParams = {
  cedantId: string;
  folderName: string;
};

type CreateFolderParams = {
  cedantId: string;
  pathId?: string;
  folderName: string;
};

export type ShowFolderDialogParams = {
  cedantId: string;
  pathId?: string;
};

type CreateFolderBag = {
  showFolderDialog(args: ShowFolderDialogParams): void;
};

export const useCreateFolder = (): CreateFolderBag => {
  const toast = useToast();
  const navigate = useNavigate();
  const [createFolderMutation] = useCreateFolderMutation();
  const [validateFilesMutation] = useValidateFilenamesMutation();
  const { openModal, updateModalContent, closeModal } = useImperativeModal();

  const showFolderDialog = ({ pathId, cedantId }: ShowFolderDialogParams) => {
    const createFolderDialog = (
      <FolderNameDialog
        onCancel={closeModal}
        onConfirm={async (folderName) =>
          await createFolder({ pathId, cedantId, folderName })
        }
      />
    );

    openModal(createFolderDialog, { size: 'md' });
  };

  const validateFolderName = async ({
    cedantId,
    folderName,
  }: ValidateFolderParams) => {
    const { data, errors } = await validateFilesMutation({
      variables: getValidateFolderMutationInput(cedantId, folderName),
    });

    if (!data || errors) throw new Error('Error validating folder name');

    const validatedFolderName = head<ValidatedFilename>(data.validateFilenames);

    if (!validatedFolderName) throw new Error('Error validating folder name');

    return validatedFolderName;
  };

  const handleCreateFolder = async ({
    pathId,
    cedantId,
    folderName,
  }: CreateFolderParams) => {
    try {
      const decodedPathId = formatDirPath(pathId);
      const directoryPath = generateNewDirPath(decodedPathId, folderName);
      const { errors, data } = await createFolderMutation({
        variables: {
          cedant: cedantId,
          input: { directory: directoryPath },
        },
      });

      if (!data || errors) {
        throw new Error('Failed to create folder');
      }

      const navigationPath = navigateToDirectory({
        cedantId,
        directoryId: data.createFolder.id,
      });

      navigate(navigationPath);
    } catch {
      toast(ToastConfigs.unexpectedErrorToastOpts);
    } finally {
      closeModal();
    }
  };

  const createFolder = async ({
    pathId,
    cedantId,
    folderName,
  }: CreateFolderParams) => {
    try {
      const { isValid, errors } = await validateFolderName({
        cedantId,
        folderName,
      });

      if (!isValid) {
        if (!errors) {
          throw new Error('Error validating files');
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [_, suggestedFolderName] = getSuggestedFilepathAndFilename(
          errors
        );

        const createFolderValidationDialog = (
          <CreateFolderValidationDialog
            onCancel={closeModal}
            folderName={folderName}
            onConfirm={async (suggestedFolderName) =>
              await handleCreateFolder({
                pathId,
                cedantId,
                folderName: suggestedFolderName,
              })
            }
            suggestedFolderName={suggestedFolderName}
          />
        );
        return updateModalContent(createFolderValidationDialog, { size: 'xl' });
      }

      await handleCreateFolder({ pathId, cedantId, folderName });
    } catch {
      toast(ToastConfigs.unexpectedErrorToastOpts);
    }
  };

  return { showFolderDialog };
};
